import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  CheckCircle2,
  Award,
  Heart,
  Users,
  ChevronRight,
  Facebook,
  ExternalLink,
} from "lucide-react";
import LadyWithLaundryBasket from '@/assets/scrubbles-about.jpg'

const AboutPage = () => {
  // Load Facebook SDK
  useEffect(() => {
    // Load Facebook SDK
    const loadFacebookSDK = () => {
      if (window.FB) {
        window.FB.XFBML.parse();
        return;
      }

      window.fbAsyncInit = function() {
        window.FB.init({
          xfbml: true,
          version: 'v18.0'
        });
        window.FB.XFBML.parse();
      };

      const script = document.createElement('script');
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      document.body.appendChild(script);
    };

    loadFacebookSDK();

    return () => {
      // Cleanup if needed
    };
  }, []);

  const values = [
    {
      icon: Heart,
      title: "Family Owned",
      description: "Established in 2010, we're family owned and operated, treating every customer like family.",
    },
    {
      icon: Award,
      title: "Award Winning",
      description: "Multiple time Record Readers' Choice Award winner, recognized for excellence in service.",
    },
    {
      icon: Users,
      title: "Community Focused",
      description: "Active contributors to community fundraisers and working with Woolwich Community Services.",
    },
  ];

  const howItWorks = [
    {
      step: 1,
      title: "Arrive with your laundry",
      description: "Bring your clothes in a bag or basket. Sort by color and fabric type.",
    },
    {
      step: 2,
      title: "Choose your washer",
      description: "Select from various sizes (10lb - 60lb). Don't overstuff - clothes need room to wash properly.",
    },
    {
      step: 3,
      title: "Add detergent & payment",
      description: "Use our soft water and high-efficiency machines. Scan the QR code to get app on your mobile device or Get change from our machine by the door.",
    },
    {
      step: 4,
      title: "Press start",
      description: "Make sure the door is closed properly. Watch to confirm it started before walking away.",
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="bg-gradient-to-br from-white via-sky-50 to-sky-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-sky-600 font-medium text-sm uppercase tracking-wider">About Us</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mt-3">
              So Much More Than Just a Laundromat
            </h1>
            <p className="text-slate-600 mt-6 text-lg leading-relaxed">
              Established in 2010, Scrubbles is family owned, family run and partially attended. 
              We pride ourselves in our cleanliness, customer service and our state-of-the-art equipment. 
              As well as our many contributions to community fundraisers and working with Woolwich 
              Community Services to help those in need of a hand.
            </p>
            <div className="relative mt-8">
              <img
                src={LadyWithLaundryBasket}
                alt="Scrubbles Laundry Shop"
                className="shadow-xl"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-white rounded-lg">
                <p className="mx-4 text-center text-lg md:text-xl font-medium">
                  "FRESH GEAR, DRY CLEANING, ALTERATION, WASH AND FOLD, WATER REFILL, PICK UP & DELIVERY."
                </p>
              </div>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/booking">
                <Button className="bg-sky-600 hover:bg-sky-700 rounded-full px-8" data-testid="about-book-btn">
                  Book a Pickup
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Our Services Summary */}
      <section className="py-1 bg-sky-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-lg md:text-xl font-medium"></p>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sky-600 font-medium text-sm uppercase tracking-wider">Our Values</span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-3">
              What Makes Us Different
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="border-slate-100 service-card" data-testid={`value-${index}`}>
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-sky-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <value.icon className="w-8 h-8 text-sky-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{value.title}</h3>
                  <p className="text-slate-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Services Summary */}
      <section className="py-1 bg-sky-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-lg md:text-xl font-medium"></p>
        </div>
      </section>
      
      {/* How It Works */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sky-600 font-medium text-sm uppercase tracking-wider">How It Works</span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-3">
              So You've Arrived... Now What?
            </h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
              First time at our laundromat? Here's a quick guide to get you started.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((item) => (
              <div key={item.step} className="relative" data-testid={`how-it-works-${item.step}`}>
                <div className="text-6xl font-bold text-sky-100 mb-4">{item.step}</div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 bg-amber-50 rounded-2xl p-8 border border-amber-100">
            <h3 className="text-lg font-semibold text-amber-800 mb-4">Pro Tips:</h3>
            <ul className="grid md:grid-cols-2 gap-4 text-amber-700">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span>These are high-efficiency machines with soft water - go easy on the soap!</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span>Make sure nothing is stuck in the door or it will leak.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span>If your change gets stuck, press the coin return button.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span>Watch to make sure the washer started before leaving.</span>
              </li>
            </ul>
            <h3 className="text-lg font-semibold text-amber-800 mb-4 mt-8">Please be aware:</h3>
            <ul className="grid md:grid-cols-1 gap-4 text-amber-700">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span>The laundromat doors lock automatically at 11pm sharp!</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span>Lights go out and the alarm is set.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span>Anyone in the store past 11pm will trigger the alarm!</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span>Last wash and dry must be loaded and started by 9:00pm</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span>Last wash only must be loaded and started by 10:00pm</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Facebook Live Updates Section */}
      <section className="hidden py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-sky-600 font-medium text-sm uppercase tracking-wider">Stay Connected</span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-3">
              Live Updates
            </h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
              Follow us on Facebook for the latest news, special offers, and community updates.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 items-start">
            {/* Facebook Page Plugin */}
            <div className="lg:col-span-2">
              <Card className="border-slate-200 overflow-hidden shadow-lg">
                <CardContent className="p-0">
                  <div 
                    className="fb-page-container bg-white"
                    style={{ minHeight: '500px' }}
                    data-testid="facebook-feed"
                  >
                    <div
                      className="fb-page"
                      data-href="https://www.facebook.com/Scrubbleslaundry/"
                      data-tabs="timeline"
                      data-width=""
                      data-height="500"
                      data-small-header="false"
                      data-adapt-container-width="true"
                      data-hide-cover="false"
                      data-show-facepile="true"
                    >
                      <blockquote
                        cite="https://www.facebook.com/Scrubbleslaundry/"
                        className="fb-xfbml-parse-ignore"
                      >
                        <div className="flex flex-col items-center justify-center h-[500px] bg-slate-50">
                          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                            <Facebook className="w-8 h-8 text-blue-600" />
                          </div>
                          <p className="text-slate-600 text-center px-4">
                            Loading Facebook feed...
                          </p>
                          <a
                            href="https://www.facebook.com/Scrubbleslaundry/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hidden mt-4 text-blue-600 hover:text-blue-700 flex items-center gap-2"
                          >
                            Visit our Facebook page
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                      </blockquote>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Follow Us Card */}
            <div className="space-y-6">
              <Card className="border-slate-200 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-6 text-white">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                      <Facebook className="w-7 h-7" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">Scrubbles Laundry</h3>
                      <p className="text-blue-100 text-sm">@Scrubbleslaundry</p>
                    </div>
                  </div>
                  <p className="text-blue-100 text-sm mb-4">
                    Join our community for exclusive deals, laundry tips, and local updates!
                  </p>
                  <a
                    href="https://www.facebook.com/Scrubbleslaundry/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-white text-blue-600 px-6 py-3 rounded-full font-medium hover:bg-blue-50 transition-colors w-full justify-center"
                    data-testid="facebook-follow-btn"
                  >
                    <Facebook className="w-5 h-5" />
                    Follow Us
                  </a>
                </div>
              </Card>

              <Card className="border-slate-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Why Follow Us?</h3>
                  <ul className="space-y-3">
                    {[
                      "Special promotions & discounts",
                      "New service announcements",
                      "Community events & news",
                      "Helpful laundry tips",
                      "Behind-the-scenes updates",
                    ].map((item, index) => (
                      <li key={index} className="flex items-center gap-3 text-sm text-slate-600">
                        <div className="w-5 h-5 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                          <CheckCircle2 className="w-3 h-3 text-sky-600" />
                        </div>
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-slate-200 shadow-lg bg-gradient-to-br from-sky-50 to-white">
                <CardContent className="p-6 text-center">
                  <p className="text-slate-600 text-sm mb-3">
                    Have questions? Message us on Facebook for quick responses!
                  </p>
                  <a
                    href="https://m.me/Scrubbleslaundry"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sky-600 hover:text-sky-700 font-medium text-sm"
                  >
                    Send us a message
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-sky-600 to-sky-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white">
            Visit Us Today
          </h2>
          <p className="text-sky-100 mt-4 text-lg">
            Open 6am - 11pm daily
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/booking">
              <Button size="lg" className="bg-white text-sky-700 hover:bg-sky-50 rounded-full px-8" data-testid="about-cta-book-btn">
                Book a Pickup
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8" data-testid="about-cta-contact-btn">
                Get Directions
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
